import { FC, useCallback, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Box, Cell, CustomModalLayout, FormField, Input, Layout, Modal } from 'wix-style-react';
import { classes } from './AccountSettingsModal.st.css';
import { API_INSTANCE, buildURL } from 'src/features/utils';
import { selectSiteData } from 'src/features/account-slice';
import { Copy } from 'src/modules/events/components/copy';
import { ConfirmPopup } from 'primereact/confirmpopup';
import { Toast } from 'primereact/toast';
import { PinVerificationModal } from 'src/modules/events/pin-verification-modal';
import { useAppSelector } from 'src/hooks';
import { accountSelector } from 'src/redesign/features/Account/store/selectors';

type AccountFormData = {
  first_name: string;
  last_name: string;
  handle: string;
  display_name: string;
  personal_website_url: string;
  linkedin_profile_url: string;
  youtube_profile_url: string;
  useCustomSiteHandle: boolean;
};

type AccountSettingsModalProps = {
  isVisible?: boolean;
  setVisible: (value: boolean) => void;
};

export const AccountSettingsModal: FC<AccountSettingsModalProps> = ({ isVisible, setVisible }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [newConfirmPassword, setNewConfirmPassword] = useState('');
  const site = useSelector(selectSiteData);
  const account = useAppSelector(accountSelector);

  const { handleSubmit, control } = useForm<AccountFormData>({
    defaultValues: {
      first_name: '',
      last_name: '',
      handle: '',
      personal_website_url: '',
      display_name: '',
      linkedin_profile_url: '',
      youtube_profile_url: '',
      ...account,
    },
  });

  const closeModal = () => {
    setVisible(false);
  };

  const onChangePassword = useCallback(
    async (pinConfirmationData, captchaToken) => {
      try {
        if (newPassword !== newConfirmPassword) {
          setError('The new passwords do not match. Please try again.');
          return;
        }
        if (newPassword.length <= 8) {
          setError('The password must be longer than 8 characters.');
          return;
        }

        await API_INSTANCE.post(`/api/v1/dashboard/accounts/change-password`, {
          new_password: newPassword,
          new_password_confirm: newConfirmPassword,
          verificationCode: pinConfirmationData.verificationCode,
          'g-recaptcha-response': captchaToken,
        });

        setNewPassword('');
        setNewConfirmPassword('');
        setError(null);
        toast.current.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Your password has been changed successfully.',
          life: 3000,
        });
      } catch (err) {
        if (err.response?.status === 401) {
          setError('The current password is incorrect. Please try again.');
        } else if (err.response?.data?.message) {
          setError(err.response.data.message);
        } else {
          console.error(err);
          setError(
            'An unexpected error occurred while changing the password. Please try again later.'
          );
        }
      }
    },
    [site, newPassword, newConfirmPassword]
  );

  const onSubmit: SubmitHandler<AccountFormData> = async (data) => {
    setLoading(true);

    try {
      const newData = data;
      await API_INSTANCE.put(`/api/v1/dashboard/accounts`, newData);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);

      toast.current.show({
        severity: 'success',
        summary: 'Update Account',
        detail: 'Successfully Saved Changes',
        life: 3000,
      });
    }
  };

  const toast = useRef(null);

  const confirmChangePassword = (s, data, captchaToken) => {
    onChangePassword(data, captchaToken);
  };

  const renderForm = () => (
    <Layout>
      <Cell span={12}>
        <Box direction="vertical" gap={2}>
          <div className={classes.heading}>Email</div>
          <Copy text={account?.email} />
        </Box>
        <Box marginTop={2}>
          <div className={classes.heading}>Profile</div>
        </Box>
        <Cell span={12}>
          <Box gap={2} direction="vertical" marginTop={2} marginBottom={3}>
            <ProfileForm control={control} />
          </Box>
        </Cell>
        <Box direction="vertical" marginTop={4} marginBottom={2}>
          <div className={classes.heading}>Security</div>
        </Box>
        <Cell span={12}>
          <Layout>
            <Cell span={12}>
              <Box gap={2} direction="vertical" className="account-settings-modal">
                <FormField label="">
                  <Input
                    size="large"
                    placeholder="New password"
                    value={newPassword}
                    name="new-password"
                    type="password"
                    onChange={(e) => setNewPassword(e.currentTarget.value)}
                    status={error ? 'error' : undefined}
                  />
                </FormField>
                <FormField label="">
                  <Input
                    size="large"
                    placeholder="Confirm New Password"
                    value={newConfirmPassword}
                    name="confirm-password"
                    type="password"
                    onChange={(e) => setNewConfirmPassword(e.currentTarget.value)}
                    status={error ? 'error' : undefined}
                  />
                </FormField>
              </Box>
            </Cell>
            <Cell span={12}>
              <Box width="100%" align="center" gap={2} direction="vertical">
                <Toast ref={toast} />
                <ConfirmPopup />

                <PinVerificationModal
                  disabled={
                    newPassword === '' ||
                    newPassword === null ||
                    newConfirmPassword === '' ||
                    newConfirmPassword === null
                  }
                  buttonVerifyButtonText="Confirm Password Change"
                  includeCaptcha
                  className={classes.button}
                  validate={() => newPassword === newConfirmPassword}
                  validationFailedText="Passwords do not match"
                  codeSuccesfulVerificationUrl={buildURL(
                    site,
                    `/api/v1/dashboard/accounts/change-password-code-verification`
                  )}
                  buttonText="Change Password"
                  pinVerifiedCallback={confirmChangePassword}
                  email={account.email}
                  autoOpen={false}
                />
                {error && <Box className={classes.error}>{error}</Box>}
              </Box>
            </Cell>
          </Layout>
        </Cell>
      </Cell>
    </Layout>
  );

  const renderModalContent = () => (
    <CustomModalLayout
      primaryButtonText="Save"
      primaryButtonProps={{ disabled: loading }}
      secondaryButtonText="Cancel"
      onCloseButtonClick={closeModal}
      primaryButtonOnClick={handleSubmit(onSubmit)}
      secondaryButtonOnClick={closeModal}
      title={`Account Settings`}
      width="600px"
      height="700px"
    >
      {renderForm()}
    </CustomModalLayout>
  );

  return (
    <>
      <Modal isOpen={isVisible} onRequestClose={closeModal} screen="desktop">
        {renderModalContent()}
      </Modal>
    </>
  );
};

const ProfileForm: FC<{ control: any }> = ({ control }) => (
  <>
    <FormField label="First Name">
      <Controller
        control={control}
        name="first_name"
        render={({ field }) => <Input {...field} />}
      />
    </FormField>
    <FormField label="Last Name">
      <Controller control={control} name="last_name" render={({ field }) => <Input {...field} />} />
    </FormField>
    <FormField label="Display Name">
      <Controller
        control={control}
        name="display_name"
        render={({ field }) => <Input {...field} />}
      />
    </FormField>

    <FormField label="Personal Website">
      <Controller
        control={control}
        name="personal_website_url"
        render={({ field }) => <Input {...field} />}
      />
    </FormField>
    <FormField label="Linkedin Profile">
      <Controller
        control={control}
        name="linkedin_profile_url"
        render={({ field }) => <Input {...field} />}
      />
    </FormField>
    <FormField label="Youtube Profile">
      <Controller
        control={control}
        name="youtube_profile_url"
        render={({ field }) => <Input {...field} />}
      />
    </FormField>
  </>
);
